import './assets/tailwind.css'
import './assets/main.css'
import 'vue-toastification/dist/index.css'
import './assets/nprogress.css'

import { createApp as createClientApp, createSSRApp } from 'vue'
import { createPinia } from 'pinia'
import {
  MutationCache,
  QueryCache,
  VueQueryPlugin,
  type VueQueryPluginOptions
} from '@tanstack/vue-query'
import Toast, { useToast } from 'vue-toastification'
import moment from 'moment'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import App from './App.vue'
import { createAppRouter } from './router'
import { registerPiniaPlugins } from './plugins/piniaStore.js'
import vTrack from './directives/v-track'
import { i18n } from './i18n'
import { installUserFriendlyErrorHandling } from './plugins/user-friendly-errors'
import { getUserFriendlyErrorMessage } from './utils/error-message'

moment.locale('ar-kw')
moment.updateLocale('ar-kw', {
  week: {
    dow: 0,
    doy: 6
  }
})

function isCancelledOperation(error: unknown) {
  if (!error || typeof error !== 'object') return false

  const candidate = error as { code?: unknown; name?: unknown; message?: unknown }
  const message = typeof candidate.message === 'string' ? candidate.message.toLowerCase() : ''

  return (
    candidate.code === 'ERR_CANCELED' ||
    candidate.name === 'AbortError' ||
    candidate.name === 'CanceledError' ||
    candidate.name === 'CancelledError' ||
    ['cancelled', 'canceled', 'cancellederror', 'cancelederror', 'refused to cancel evaluation'].includes(
      message
    )
  )
}

function notifyQueryError(error: unknown) {
  if (!import.meta.env.SSR && !isCancelledOperation(error)) {
    useToast().error(getUserFriendlyErrorMessage(error))
  }
}

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    queryCache: new QueryCache({
      onError: (error, query) => {
        // A failed background refetch should not interrupt users who can still
        // work with cached data. Initial page-load failures must be visible.
        if (query.state.data === undefined) notifyQueryError(error)
      }
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Page-specific mutation handlers can provide operation-specific copy.
        // This covers mutations that otherwise fail silently.
        if (!mutation.options.onError) notifyQueryError(error)
      }
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false
      }
    }
  }
}

export function createMaqariApp() {
  const app = import.meta.env.SSR ? createSSRApp(App) : createClientApp(App)
  const pinia = createPinia()
  const router = createAppRouter()

  registerPiniaPlugins(app, pinia)

  app
    .use(VueQueryPlugin, vueQueryPluginOptions)
    .use(i18n)
    .use(router)
    .use(Toast, {
      rtl: true
    })
    .use(autoAnimatePlugin)

  installUserFriendlyErrorHandling(app)

  app.directive('track', vTrack)

  return { app, router, pinia }
}
